// /var/www/oszczedzam/client/src/pages/Transactions.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Box, Typography, Paper, Button, Dialog, DialogTitle, 
  DialogContent, DialogContentText, DialogActions, Tab, Tabs, 
  CircularProgress, Alert, Snackbar, Grid, Backdrop
} from '@mui/material';
import { 
  Add as AddIcon,
  CloudUpload as CloudUploadIcon 
} from '@mui/icons-material';
import TransactionForm from '../components/transactions/TransactionForm';
import TransactionList from '../components/transactions/TransactionList';
import TransactionFilters from '../components/transactions/TransactionFilters';
import ImportTransactions from '../components/transactions/ImportTransactions';
import { database } from '../firebase';
import { ref, push, get, update, remove, query, orderByChild } from 'firebase/database';
import { useAuth } from '../contexts/AuthContext';

function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [currentFilters, setCurrentFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [bulkOperationLoading, setBulkOperationLoading] = useState(false);
  const [error, setError] = useState('');
  const [openForm, setOpenForm] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [availableTags, setAvailableTags] = useState([]);
  const [notification, setNotification] = useState({ open: false, message: '', type: 'success' });
  const { currentUser } = useAuth();
  
  // Stan dla dialogu importu transakcji
  const [openImportDialog, setOpenImportDialog] = useState(false);

  // Memoized dostępne tagi
  const memoizedAvailableTags = useMemo(() => {
    const tagsSet = new Set();
    transactions.forEach(transaction => {
      if (transaction.tags && Array.isArray(transaction.tags)) {
        transaction.tags.forEach(tag => tagsSet.add(tag));
      }
    });
    return Array.from(tagsSet);
  }, [transactions]);

  // Aktualizuj availableTags przy zmianie transactions
  useEffect(() => {
    setAvailableTags(memoizedAvailableTags);
  }, [memoizedAvailableTags]);

  // Funkcja aplikowania filtrów
  const applyFilters = useCallback((filters = currentFilters, transactionsList = transactions, currentTab = tabValue) => {
    let filtered = [...transactionsList];
    
    // Filtruj według daty od
    if (filters.dateFrom) {
      filtered = filtered.filter(t => t.date >= filters.dateFrom);
    }
    
    // Filtruj według daty do
    if (filters.dateTo) {
      filtered = filtered.filter(t => t.date <= filters.dateTo);
    }
    
    // Filtruj według kategorii
    if (filters.category) {
      filtered = filtered.filter(t => t.category === filters.category);
    }
    
    // Filtruj według tytułu
    if (filters.title) {
      const searchTerm = filters.title.toLowerCase();
      filtered = filtered.filter(t => 
        (t.title && t.title.toLowerCase().includes(searchTerm)) ||
        (t.description && t.description.toLowerCase().includes(searchTerm))
      );
    }
    
    // Filtruj według typu transakcji
    if (filters.transactionType) {
      if (filters.transactionType === 'income') {
        filtered = filtered.filter(t => t.amount > 0);
      } else if (filters.transactionType === 'expense') {
        filtered = filtered.filter(t => t.amount < 0);
      }
    }
    
    // Filtruj według tagów
    if (filters.tags && filters.tags.length > 0) {
      filtered = filtered.filter(t => 
        t.tags && filters.tags.every(tag => t.tags.includes(tag))
      );
    }
    
    // Filtruj według minimalnej kwoty
    if (filters.amountMin !== null && filters.amountMin !== undefined) {
      filtered = filtered.filter(t => Math.abs(t.amount) >= filters.amountMin);
    }
    
    // Filtruj według maksymalnej kwoty
    if (filters.amountMax !== null && filters.amountMax !== undefined) {
      filtered = filtered.filter(t => Math.abs(t.amount) <= filters.amountMax);
    }
    
    // Filtruj według cykliczności
    if (filters.isRecurring !== null && filters.isRecurring !== undefined) {
      filtered = filtered.filter(t => t.isRecurring === filters.isRecurring);
    }
    
    // Zastosuj filtrowanie według zakładki
    if (currentTab === 1) { // Przychody
      filtered = filtered.filter(t => t.amount > 0);
    } else if (currentTab === 2) { // Wydatki
      filtered = filtered.filter(t => t.amount < 0);
    }
    
    return filtered;
  }, [currentFilters, transactions, tabValue]);

  // Obsługa zakończenia importu
  const handleImportComplete = useCallback((summary) => {
    setOpenImportDialog(false);
    showNotification(`Pomyślnie zaimportowano ${summary.successful} transakcji`, 'success');
    // Odśwież listę transakcji po imporcie
    fetchTransactions();
  }, []);

  // Funkcja pobierania transakcji
  const fetchTransactions = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const transactionsRef = ref(database, `users/${currentUser.uid}/transactions`);
      const snapshot = await get(transactionsRef);
      
      const transactionsData = [];
      
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const transaction = {
            id: childSnapshot.key,
            ...childSnapshot.val()
          };
          
          // Walidacja transakcji
          if (transaction.amount !== undefined && transaction.title && transaction.date) {
            transactionsData.push(transaction);
          }
        });
      }
      
      setTransactions(transactionsData);
      setError('');
    } catch (err) {
      console.error("Błąd podczas pobierania transakcji:", err);
      setError('Nie udało się pobrać transakcji. Spróbuj ponownie później.');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Pobierz transakcje z bazy danych
  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Aplikuj filtry przy zmianie transakcji lub filtrów
  useEffect(() => {
    const filtered = applyFilters(currentFilters, transactions, tabValue);
    setFilteredTransactions(filtered);
  }, [transactions, currentFilters, tabValue, applyFilters]);

  // Obsługa zmiany zakładki
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Dodaj nową transakcję
  const handleAddTransaction = async (transactionData) => {
    try {
      // Walidacja danych
      if (!transactionData.title || !transactionData.amount || !transactionData.category || !transactionData.date) {
        showNotification('Wszystkie wymagane pola muszą być wypełnione.', 'error');
        return;
      }

      const transactionsRef = ref(database, `users/${currentUser.uid}/transactions`);
      await push(transactionsRef, {
        ...transactionData,
        createdAt: new Date().toISOString()
      });
      
      // Odśwież listę transakcji
      await fetchTransactions();
      setOpenForm(false);
      showNotification('Transakcja została dodana pomyślnie!', 'success');
    } catch (err) {
      console.error("Błąd podczas dodawania transakcji:", err);
      showNotification('Nie udało się dodać transakcji. Spróbuj ponownie później.', 'error');
    }
  };

  // Aktualizuj transakcję
  const handleUpdateTransaction = async (transactionData) => {
    const originalTransactions = [...transactions];

    try {
      if (!editingTransaction?.id) {
        showNotification('Błąd: Nie można zaktualizować transakcji.', 'error');
        return;
      }

      // Walidacja danych
      if (!transactionData.title || !transactionData.amount || !transactionData.category || !transactionData.date) {
        showNotification('Wszystkie wymagane pola muszą być wypełnione.', 'error');
        return;
      }

      const updatedTransactionData = {
        ...transactionData,
        updatedAt: new Date().toISOString()
      };

      // Optymistyczna aktualizacja UI
      setTransactions(prevTransactions =>
        prevTransactions.map(t =>
          t.id === editingTransaction.id ? { ...t, ...updatedTransactionData } : t
        )
      );
      
      setOpenForm(false);
      setEditingTransaction(null);

      // Aktualizacja w bazie danych
      const transactionRef = ref(database, `users/${currentUser.uid}/transactions/${editingTransaction.id}`);
      await update(transactionRef, updatedTransactionData);

      showNotification('Transakcja została zaktualizowana pomyślnie!', 'success');
    } catch (err) {
      console.error("Błąd podczas aktualizacji transakcji:", err);
      showNotification('Nie udało się zaktualizować transakcji. Przywracanie zmian.', 'error');
      // Wycofaj zmiany w UI w przypadku błędu
      setTransactions(originalTransactions);
      setOpenForm(true); // Opcjonalnie: otwórz ponownie formularz
    }
  };

  // Usuń transakcję
  const handleDeleteTransaction = async () => {
    try {
      if (!transactionToDelete) {
        showNotification('Błąd: Nie można usunąć transakcji.', 'error');
        return;
      }

      const transactionRef = ref(database, `users/${currentUser.uid}/transactions/${transactionToDelete}`);
      await remove(transactionRef);
      
      // Aktualizuj stan lokalnie bez pełnego odświeżenia
      const updatedTransactions = transactions.filter(t => t.id !== transactionToDelete);
      setTransactions(updatedTransactions);
      
      setDeleteDialogOpen(false);
      setTransactionToDelete(null);
      showNotification('Transakcja została usunięta pomyślnie!', 'success');
    } catch (err) {
      console.error("Błąd podczas usuwania transakcji:", err);
      showNotification('Nie udało się usunąć transakcji. Spróbuj ponownie później.', 'error');
    }
  };

  // Otwórz formularz edycji transakcji
  const handleEditTransaction = (transaction) => {
    if (!transaction?.id) {
      showNotification('Błąd: Nie można edytować transakcji.', 'error');
      return;
    }
    setEditingTransaction(transaction);
    setOpenForm(true);
  };

  // Otwórz dialog potwierdzenia usunięcia
  const handleOpenDeleteDialog = (transactionId) => {
    if (!transactionId) {
      showNotification('Błąd: Nie można usunąć transakcji.', 'error');
      return;
    }
    setTransactionToDelete(transactionId);
    setDeleteDialogOpen(true);
  };

  // Zastosuj filtry - zapisz je w stanie i aplikuj
  const handleApplyFilters = useCallback((filters) => {
    setCurrentFilters(filters);
  }, []);

  // Zmiana kategorii dla grupy transakcji z zachowaniem filtrów
  const handleBulkCategoryChange = async (transactionIds, newCategory) => {
    if (!transactionIds?.length || !newCategory) {
      showNotification('Błąd: Nieprawidłowe dane dla masowej zmiany kategorii.', 'error');
      return;
    }

    const originalTransactions = [...transactions];
    setBulkOperationLoading(true);

    // Optymistyczna aktualizacja UI
    const newUpdatedAt = new Date().toISOString();
    const validIds = transactionIds.filter(id =>
      transactions.some(t => t.id === id)
    );

    setTransactions(prevTransactions =>
      prevTransactions.map(t =>
        validIds.includes(t.id)
          ? { ...t, category: newCategory, updatedAt: newUpdatedAt }
          : t
      )
    );

    try {
      let successCount = 0;
      let errorCount = 0;
      
      if (validIds.length === 0) {
        showNotification('Błąd: Nie znaleziono wybranych transakcji.', 'error');
        setTransactions(originalTransactions); // Wycofaj zmiany
        return;
      }

      const updatePromises = validIds.map(id => {
        const transactionRef = ref(database, `users/${currentUser.uid}/transactions/${id}`);
        return update(transactionRef, {
          category: newCategory,
          updatedAt: newUpdatedAt
        }).then(() => {
          successCount++;
        }).catch(error => {
          console.error(`Błąd podczas aktualizacji transakcji ${id}:`, error);
          errorCount++;
        });
      });

      await Promise.all(updatePromises);
      
      // Pokazuj odpowiednie powiadomienie
      if (successCount > 0) {
        const message = errorCount > 0 
          ? `Zmieniono kategorię dla ${successCount} transakcji. ${errorCount} operacji nie powiodło się.`
          : `Zmieniono kategorię dla ${successCount} transakcji.`;
        showNotification(message, errorCount > 0 ? 'warning' : 'success');
      } else {
        showNotification('Nie udało się zmienić kategorii dla żadnej transakcji.', 'error');
      }

      if (errorCount > 0) {
        // Jeśli wystąpiły błędy, pobierz świeże dane, aby zapewnić spójność
        await fetchTransactions();
      }

    } catch (err) {
      console.error("Błąd podczas masowej zmiany kategorii:", err);
      showNotification('Wystąpił błąd podczas zmiany kategorii. Przywracanie zmian.', 'error');
      setTransactions(originalTransactions); // Wycofaj wszystkie zmiany w UI
    } finally {
      setBulkOperationLoading(false);
    }
  };

  // Pokaż powiadomienie
  const showNotification = useCallback((message, type = 'success') => {
    setNotification({
      open: true,
      message,
      type
    });
  }, []);

  // Zamknij powiadomienie
  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">Transakcje</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            startIcon={<CloudUploadIcon />}
            onClick={() => setOpenImportDialog(true)}
            disabled={loading || bulkOperationLoading}
          >
            Import z mBanku
          </Button>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingTransaction(null);
              setOpenForm(true);
            }}
            disabled={loading || bulkOperationLoading}
          >
            Nowa transakcja
          </Button>
        </Box>
      </Box>
      
      {/* Filtry */}
      <TransactionFilters 
        onApplyFilters={handleApplyFilters}
        availableTags={availableTags}
      />
      
      {/* Zakładki */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Wszystkie" />
          <Tab label="Przychody" />
          <Tab label="Wydatki" />
        </Tabs>
      </Box>
      
      {/* Komunikat o błędzie */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Lista transakcji */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TransactionList 
          transactions={filteredTransactions}
          onEdit={handleEditTransaction}
          onDelete={handleOpenDeleteDialog}
          onBulkCategoryChange={handleBulkCategoryChange}
          loading={bulkOperationLoading}
        />
      )}
      
      {/* Backdrop dla masowych operacji */}
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={bulkOperationLoading}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress color="inherit" sx={{ mb: 2 }} />
          <Typography variant="h6">Aktualizowanie kategorii...</Typography>
        </Box>
      </Backdrop>
      
      {/* Formularz transakcji */}
      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="md" fullWidth>
        <DialogContent>
          <TransactionForm 
            onSubmit={editingTransaction ? handleUpdateTransaction : handleAddTransaction}
            initialData={editingTransaction}
            isEditing={!!editingTransaction}
          />
        </DialogContent>
      </Dialog>
      
      {/* Dialog potwierdzenia usunięcia */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Potwierdź usunięcie</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Czy na pewno chcesz usunąć tę transakcję? Tej operacji nie można cofnąć.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Anuluj</Button>
          <Button onClick={handleDeleteTransaction} color="error" autoFocus>
            Usuń
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Powiadomienia */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.type} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      
      {/* Dialog importu transakcji */}
      <Dialog
        open={openImportDialog}
        onClose={() => setOpenImportDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent>
          <ImportTransactions 
            onImportComplete={handleImportComplete}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default Transactions;